import React, { useState, useRef, useEffect } from 'react';

const RoleTypeSlider = ({ 
  value = 'lead', // Changed from 'principal' to 'lead' (valid enum value)
  onChange, 
  disabled = false,
  className = '' 
}) => {
  // Define role types using only VALID database enum values: ['lead', 'supporting', 'ensemble', 'understudy', 'swing']
  // Ordered from Principal-like to Ensemble as requested
  const roleTypes = [
    { value: 'lead', label: 'Lead' }, // Lead is the closest to Principal in the database
    { value: 'supporting', label: 'Supporting' },
    { value: 'understudy', label: 'Understudy' },
    { value: 'swing', label: 'Swing' },
    { value: 'ensemble', label: 'Ensemble' }
  ];

  const [currentIndex, setCurrentIndex] = useState(
    roleTypes?.findIndex(type => type?.value === value) || 0
  );
  const [isDragging, setIsDragging] = useState(false);
  
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);

  // Update index when value prop changes
  useEffect(() => {
    const newIndex = roleTypes?.findIndex(type => type?.value === value);
    if (newIndex !== -1) {
      setCurrentIndex(newIndex);
    }
  }, [value]);

  // Notify parent of changes
  useEffect(() => {
    onChange?.(roleTypes?.[currentIndex]?.value);
  }, [currentIndex, onChange]);

  const getPercentage = (index) => {
    return (index / (roleTypes?.length - 1)) * 100;
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || disabled || !sliderRef?.current) return;
    
    e?.preventDefault();
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((e?.clientX - rect?.left) / rect?.width) * 100));
    const newIndex = Math.round((percentage / 100) * (roleTypes?.length - 1));
    
    setCurrentIndex(newIndex);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const handleTrackClick = (e) => {
    if (disabled || isDragging) return;
    
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = ((e?.clientX - rect?.left) / rect?.width) * 100;
    const clickIndex = Math.round((percentage / 100) * (roleTypes?.length - 1));
    
    setCurrentIndex(clickIndex);
  };

  return (
    <div className={`role-type-slider ${className}`}>
      {/* Current Selection Display */}
      <div className="mb-3">
        <span className="text-sm font-medium text-blue-100">
          {roleTypes?.[currentIndex]?.label}
        </span>
      </div>
      
      {/* Slider Track */}
      <div 
        ref={sliderRef}
        className="relative h-6 bg-white bg-opacity-20 rounded-lg cursor-pointer"
        onClick={handleTrackClick}
      >
        {/* Progress Track */}
        <div
          className="absolute h-full bg-gradient-to-r from-green-500 to-teal-500 rounded-lg"
          style={{
            width: `${getPercentage(currentIndex)}%`
          }}
        />
        
        {/* Thumb */}
        <div
          ref={thumbRef}
          className={`absolute w-6 h-6 bg-white rounded-full shadow-lg cursor-grab transform -translate-y-0 -translate-x-1/2 border-2 border-green-500 ${isDragging ? 'cursor-grabbing scale-110' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${getPercentage(currentIndex)}%` }}
          onMouseDown={handleMouseDown}
        />
      </div>
      
      {/* Role Type Labels - Updated to match valid database enum values */}
      <div className="flex justify-between mt-2 text-xs text-blue-200">
        <span>Lead</span>
        <span>Supporting</span>
        <span>Understudy</span>
        <span>Swing</span>
        <span>Ensemble</span>
      </div>
    </div>
  );
};

export default RoleTypeSlider;